import { useMemo, useState } from 'react';
import { Edit2, Plus, Check, X, Search, Sliders, AlertCircle, RefreshCw } from 'lucide-react';
import {
  useFeaturesMatrix,
  useToggleFeature,
  useCreateFeature,
  useUpdateFeature,
} from '@/hooks/useFeatures';
import { SearchableSelect } from '@components/common/SearchableSelect';
import type { Feature, FeatureCreatePayload, FeatureUpdatePayload, Plan } from '@/types/features';

type FeatureFormState = {
  key: string;
  display_name: string;
  category: string;
  sort_order: string;
  description: string;
  is_active: boolean;
};

const EMPTY_FEATURE_FORM: FeatureFormState = {
  key: '',
  display_name: '',
  category: 'core',
  sort_order: '0',
  description: '',
  is_active: true,
};

const FEATURE_CATEGORY_OPTIONS = [
  { id: 'core', label: 'Core AP features' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'billing', label: 'Billing & Pricing' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'custom', label: 'Custom Extensions' },
];

export function FeaturesCatalogPanel() {
  const matrixQuery = useFeaturesMatrix();
  const toggleFeature = useToggleFeature();
  const createFeature = useCreateFeature();
  const updateFeature = useUpdateFeature();

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');

  // Inline edit state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formState, setFormState] = useState<FeatureFormState>(EMPTY_FEATURE_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  // Track pending check-toggles to display inline loaders
  const [pendingToggles, setPendingToggles] = useState<Record<string, boolean>>({});

  // Extract plans and matrix row arrays safely
  const plans = matrixQuery.data?.plans ?? [];
  const matrix = matrixQuery.data?.matrix ?? [];

  // Extract all categories dynamically for tabs
  const categories = useMemo(() => {
    const cats = new Set<string>();
    matrix.forEach((row) => {
      if (row.feature.category) {
        cats.add(row.feature.category);
      } else {
        cats.add('uncategorized');
      }
    });
    return Array.from(cats).sort();
  }, [matrix]);

  // Group and filter the feature matrix rows
  const filteredMatrix = useMemo(() => {
    return matrix.filter((row) => {
      const keyMatch = row.feature.key.toLowerCase().includes(searchQuery.toLowerCase());
      const nameMatch = row.feature.display_name.toLowerCase().includes(searchQuery.toLowerCase());
      const descMatch = (row.feature.description ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      const searchMatch = keyMatch || nameMatch || descMatch;

      if (!searchMatch) return false;

      if (activeCategory !== 'all') {
        const cat = row.feature.category || 'uncategorized';
        if (cat !== activeCategory) return false;
      }

      return true;
    });
  }, [matrix, searchQuery, activeCategory]);

  const groupedMatrix = useMemo(() => {
    const groups: Record<string, typeof matrix> = {};
    filteredMatrix.forEach((row) => {
      const cat = row.feature.category || 'uncategorized';
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(row);
    });

    // Sort rows inside each group by sort_order
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => a.feature.sort_order - b.feature.sort_order);
    });

    return groups;
  }, [filteredMatrix]);

  // Handle toggle switch action
  async function handleToggle(planId: string, featureKey: string, currentStatus: boolean) {
    const toggleKey = `${planId}_${featureKey}`;
    setPendingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      await toggleFeature.mutateAsync({
        plan_id: planId,
        feature_key: featureKey,
        is_enabled: !currentStatus,
      });
    } catch (err) {
      console.error('Failed to toggle feature entitlement', err);
    } finally {
      setPendingToggles((prev) => {
        const copy = { ...prev };
        delete copy[toggleKey];
        return copy;
      });
    }
  }

  // Edit metadata trigger
  function startInlineEdit(feature: Feature) {
    setEditingKey(feature.key);
    setEditDisplayName(feature.display_name);
    setEditDescription(feature.description ?? '');
    setEditCategory(feature.category ?? 'core');
    setEditSortOrder(String(feature.sort_order));
    setInlineError(null);
  }

  // Save inline metadata
  async function saveInlineEdit(key: string) {
    if (!editDisplayName.trim()) {
      setInlineError('Display name is required.');
      return;
    }

    try {
      const payload: FeatureUpdatePayload = {
        display_name: editDisplayName.trim(),
        description: editDescription.trim() || null,
        category: editCategory.trim() || 'core',
        sort_order: Number(editSortOrder) || 0,
      };

      await updateFeature.mutateAsync({ key, body: payload });
      setEditingKey(null);
      setInlineError(null);
    } catch (err: any) {
      setInlineError(err?.response?.data?.detail || err.message || 'Failed to update feature.');
    }
  }

  // Save manual feature form
  async function handleCreateFeature() {
    setFormError(null);

    if (!formState.key.trim() || !formState.display_name.trim()) {
      setFormError('Feature key and display name are required.');
      return;
    }

    // Key validation
    const keyRegex = /^[a-zA-Z0-9_]+$/;
    if (!keyRegex.test(formState.key.trim())) {
      setFormError('Feature key must be alphanumeric and underscores only (e.g. invoice_export).');
      return;
    }

    try {
      const payload: FeatureCreatePayload = {
        key: formState.key.trim(),
        display_name: formState.display_name.trim(),
        description: formState.description.trim() || null,
        category: formState.category.trim() || 'core',
        sort_order: Number(formState.sort_order) || 0,
        is_active: formState.is_active,
      };

      await createFeature.mutateAsync(payload);
      setFormState(EMPTY_FEATURE_FORM);
      setIsDrawerOpen(false);
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || err.message || 'Failed to create feature.');
    }
  }

  const formatCategoryName = (cat: string) => {
    if (cat === 'auto_discovered') {
      return 'Auto-Discovered Features';
    }
    return cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, ' ');
  };

  if (matrixQuery.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <RefreshCw className="h-8 w-8 text-teal animate-spin" />
        <span className="text-content-secondary font-medium">Scanning codebase and sync catalog...</span>
      </div>
    );
  }

  if (matrixQuery.isError) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700">
        <AlertCircle size={20} />
        <div>
          <h4 className="font-semibold">Failed to load features matrix</h4>
          <p className="text-sm">{(matrixQuery.error as Error)?.message || 'Unknown network error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="features-matrix-container">
      {/* Search and Filters Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="toolbar-cluster">
          <div className="search-field">
            <Search size={18} className="text-content-muted" />
            <input
              type="text"
              placeholder="Search features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="category-tab-container">
            <button
              onClick={() => setActiveCategory('all')}
              className={`category-tab ${activeCategory === 'all' ? 'active' : ''}`}
            >
              All Features
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`category-tab ${activeCategory === cat ? 'active' : ''}`}
              >
                {formatCategoryName(cat)}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="button button-primary"
          onClick={() => setIsDrawerOpen(true)}
        >
          <Plus size={16} />
          Add Custom Feature
        </button>
      </div>

      {/* Matrix Table */}
      <div className="table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Feature Spec</th>
              {plans.map((plan) => (
                <th key={plan.id} className="features-matrix-header-cell">
                  {plan.display_name}
                </th>
              ))}
              <th style={{ width: '80px' }} aria-label="Edit metadata" />
            </tr>
          </thead>
          <tbody>
            {Object.keys(groupedMatrix).length === 0 ? (
              <tr>
                <td colSpan={plans.length + 2} className="text-center py-8 text-content-muted">
                  No features found matching the filters.
                </td>
              </tr>
            ) : (
              Object.entries(groupedMatrix).map(([category, rows]) => (
                <span key={category} style={{ display: 'table-row-group' }}>
                  {/* Category Header Row */}
                  <tr className="category-header-row">
                    <td colSpan={plans.length + 2} className="category-header-cell">
                      {formatCategoryName(category)}
                    </td>
                  </tr>

                  {/* Feature Rows */}
                  {rows.map((row) => {
                    const isEditing = editingKey === row.feature.key;
                    const isAutoDiscovered = row.feature.category === 'auto_discovered';

                    return (
                      <tr key={row.feature.key} className={isEditing ? 'selected-row' : ''}>
                        <td style={{ minWidth: '320px' }}>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <strong className="text-content-primary font-semibold text-sm">
                                {row.feature.display_name}
                              </strong>
                              <code className="text-xs px-1.5 py-0.5 rounded bg-surface-raised border border-border-subtle font-mono text-content-secondary">
                                {row.feature.key}
                              </code>
                              {isAutoDiscovered && (
                                <span className="glowing-badge">Auto Discovered</span>
                              )}
                            </div>
                            <p className="text-xs text-content-muted mt-0.5">
                              {row.feature.description || 'No description provided.'}
                            </p>

                            {/* Inline Edit Form */}
                            {isEditing && (
                              <div className="inline-edit-form" onClick={(e) => e.stopPropagation()}>
                                <label className="form-field">
                                  <span>Display Name</span>
                                  <input
                                    type="text"
                                    className="inline-edit-input"
                                    value={editDisplayName}
                                    onChange={(e) => setEditDisplayName(e.target.value)}
                                  />
                                </label>
                                <label className="form-field">
                                  <span>Description</span>
                                  <textarea
                                    className="inline-edit-textarea"
                                    rows={2}
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                  />
                                </label>
                                <div className="form-grid" style={{ gap: '0.5rem', gridTemplateColumns: '1fr 1fr' }}>
                                  <label className="form-field">
                                    <span>Category</span>
                                    <input
                                      type="text"
                                      className="inline-edit-input"
                                      value={editCategory}
                                      onChange={(e) => setEditCategory(e.target.value)}
                                    />
                                  </label>
                                  <label className="form-field">
                                    <span>Sort Order</span>
                                    <input
                                      type="number"
                                      className="inline-edit-input"
                                      value={editSortOrder}
                                      onChange={(e) => setEditSortOrder(e.target.value)}
                                    />
                                  </label>
                                </div>
                                {inlineError && <p className="form-error text-xs">{inlineError}</p>}
                                <div className="inline-edit-actions">
                                  <button
                                    type="button"
                                    className="button button-secondary inline-edit-btn"
                                    onClick={() => setEditingKey(null)}
                                  >
                                    <X size={12} /> Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="button button-primary inline-edit-btn"
                                    onClick={() => saveInlineEdit(row.feature.key)}
                                    disabled={updateFeature.isPending}
                                  >
                                    <Check size={12} /> {updateFeature.isPending ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Plan Checkboxes Matrix */}
                        {plans.map((plan) => {
                          const isEnabled = row.enabled_by_plan_id[plan.id] || false;
                          const toggleKey = `${plan.id}_${row.feature.key}`;
                          const isPending = pendingToggles[toggleKey];

                          return (
                            <td key={plan.id} className="features-matrix-cell">
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  disabled={isPending || isEditing}
                                  onChange={() => handleToggle(plan.id, row.feature.key, isEnabled)}
                                />
                                <span className="slider"></span>
                              </label>
                            </td>
                          );
                        })}

                        {/* Edit metadata action */}
                        <td>
                          {!isEditing && (
                            <button
                              type="button"
                              className="button button-secondary table-link-button"
                              onClick={() => startInlineEdit(row.feature)}
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </span>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer slide-over for creating a new custom feature */}
      {isDrawerOpen && (
        <div className="apollo-modal-backdrop" onClick={() => setIsDrawerOpen(false)}>
          <div
            className="apollo-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Add custom feature"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '640px' }}
          >
            <div className="detail-stack">
              <div className="flex items-center justify-between border-b border-border-subtle pb-3">
                <div>
                  <p className="entity-breadcrumb">Catalog</p>
                  <h2>Add Custom Feature Flag</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDrawerOpen(false)}
                  className="p-1 hover:bg-surface-raised rounded-lg text-content-muted"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="form-grid">
                <label className="form-field form-field-wide">
                  <span>Unique Feature Key</span>
                  <input
                    type="text"
                    placeholder="e.g. bulk_csv_export"
                    value={formState.key}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, key: e.target.value.toLowerCase() }))
                    }
                  />
                  <span style={{ fontSize: '0.75rem', textTransform: 'none', fontWeight: 'normal', marginTop: '0.2rem' }}>
                    Lowercase alphanumeric and underscores only. This acts as the key in code.
                  </span>
                </label>

                <label className="form-field">
                  <span>Display Name</span>
                  <input
                    type="text"
                    placeholder="e.g. Bulk CSV Export"
                    value={formState.display_name}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, display_name: e.target.value }))
                    }
                  />
                </label>

                <label className="form-field">
                  <span>Category</span>
                  <SearchableSelect
                    options={FEATURE_CATEGORY_OPTIONS}
                    value={formState.category}
                    onChange={(value) =>
                      setFormState((prev) => ({ ...prev, category: value }))
                    }
                    placeholder="Feature category"
                    clearable={false}
                  />
                </label>

                <label className="form-field">
                  <span>Sort Order</span>
                  <input
                    type="number"
                    value={formState.sort_order}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, sort_order: e.target.value }))
                    }
                  />
                </label>

                <label className="form-field">
                  <span>Status</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', height: '46px' }}>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={formState.is_active}
                        onChange={(e) =>
                          setFormState((prev) => ({ ...prev, is_active: e.target.checked }))
                        }
                      />
                      <span className="slider"></span>
                    </label>
                    <span className="text-sm font-semibold text-content-secondary">
                      Active (visible in matrix)
                    </span>
                  </div>
                </label>

                <label className="form-field form-field-wide">
                  <span>Description</span>
                  <textarea
                    rows={3}
                    placeholder="Describe what capabilities this feature unlocks for accounts..."
                    value={formState.description}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, description: e.target.value }))
                    }
                  />
                </label>
              </div>

              {formError && <p className="form-error">{formError}</p>}

              <div className="form-actions border-t border-border-subtle pt-4 justify-end">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setIsDrawerOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleCreateFeature}
                  disabled={createFeature.isPending}
                >
                  {createFeature.isPending ? 'Creating...' : 'Create Feature'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
